import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { eventsApi } from '../../api/api';
import { EmptyState, Toast, ConfirmDialog, AdminSelect, AdminMonthInput, AdminFieldInput, AdminFieldTextarea, AdminFieldSelect, AdminModalClose } from '../components/AdminUI';

const COLOR_OPTIONS = ['orange', 'green', 'red', 'purple', 'blue', 'yellow', 'indigo'];
const COLOR_HEX = {
  orange: '#f97316', green: '#22c55e', red: '#ef4444', purple: '#a855f7',
  blue: '#3b82f6', yellow: '#eab308', indigo: '#6366f1',
};
const PAGE_SIZE_OPTIONS = [6, 12, 18, 24];

const EMPTY_FORM = {
  title: '', startDate: '', endDate: '', time: '',
  location: '', description: '', imageUrl: '', mapUrl: '',
  color: 'orange', type: 'event',
};

const nextAutoColor = (count) => COLOR_OPTIONS[count % COLOR_OPTIONS.length];

const toLocalDate = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const Badge = ({ type }) => (
  <span
    className="px-2.5 py-0.5 text-white text-xs font-semibold rounded-full"
    style={{ backgroundColor: type === 'katha' ? '#f97316' : '#22c55e' }}
  >
    {type === 'katha' ? 'Katha' : 'Event'}
  </span>
);

const Input = ({ label, required, hint, clearable, onClear, ...props }) => (
  <AdminFieldInput label={label} required={required} hint={hint} clearable={clearable} onClear={onClear} {...props} />
);

const Textarea = ({ label, ...props }) => (
  <AdminFieldTextarea label={label} {...props} />
);

const directionsHref = (event) => {
  if (event.mapUrl) return event.mapUrl;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location || '')}`;
};

const getFormattedDateTimeInfo = (event) => {
  const startDate = toLocalDate(event.startDate);
  const endDate = toLocalDate(event.endDate);
  if (!startDate || !endDate) return '';
  const isMultiDay = event.startDate !== event.endDate;
  const dateOpts = { year: 'numeric', month: 'long', day: 'numeric' };
  if (isMultiDay) {
    return `${startDate.toLocaleDateString('en-US', dateOpts)} - ${endDate.toLocaleDateString('en-US', dateOpts)}`;
  }
  return `${startDate.toLocaleDateString('en-US', dateOpts)}${event.time ? ' @ ' + event.time : ''}`;
};

const DateBadge = ({ event }) => {
  const bg = COLOR_HEX[event.color] || COLOR_HEX.orange;
  const startDate = toLocalDate(event.startDate);
  if (!startDate) return null;
  return (
    <div className="text-white rounded-xl p-3.5 text-center" style={{ backgroundColor: bg }}>
      <div className="text-xs font-semibold uppercase leading-none mb-1.5">{startDate.toLocaleDateString('en-US', { weekday: 'short' })}</div>
      <div className="text-2xl font-bold leading-none mb-1.5">{startDate.getDate()}</div>
      <div className="text-xs leading-none">{startDate.toLocaleDateString('en-US', { month: 'short' })}</div>
    </div>
  );
};

/** Website-style event card + admin actions */
const EventListCard = ({ event, onEdit, onDelete }) => {
  const dateText = getFormattedDateTimeInfo(event);
  const img = event.imageUrl || event.image || '/logo.jpg';

  return (
    <div className="bg-white rounded-2xl p-4 md:p-5 shadow-sm border border-orange-100 hover:shadow-md transition-all duration-300 mb-4">
      {/* Desktop: same as website */}
      <div className="hidden md:flex gap-5 items-start">
        <div className="w-22 flex-shrink-0" style={{ width: '5.5rem' }}>
          <DateBadge event={event} />
        </div>
        <div className="w-40 flex-shrink-0">
          <img src={img} alt={event.title} className="w-full h-28 object-cover rounded-xl" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-1.5">
            <div className="text-sm font-medium" style={{ color: '#ea580c' }}>{dateText}</div>
            <Badge type={event.type} />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2 leading-snug">{event.title}</h3>
          <div className="flex items-center justify-between mb-2 gap-3">
            <div className="flex items-center text-sm text-gray-600 min-w-0">
              <i className="fas fa-map-marker-alt mr-2 flex-shrink-0" style={{ color: '#f97316' }}></i>
              <span className="truncate">{event.location}</span>
            </div>
            <a
              href={directionsHref(event)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center text-sm font-medium px-3 py-1.5 rounded-lg flex-shrink-0"
              style={{ color: '#2563eb', background: '#eff6ff' }}
            >
              <i className="fas fa-directions mr-1"></i>Directions
            </a>
          </div>
          {event.description && (
            <p className="text-gray-700 leading-relaxed text-sm line-clamp-2 mb-3">{event.description}</p>
          )}
          <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
            <button type="button" onClick={onEdit} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold" style={{ background: '#eff6ff', color: '#2563eb' }}>
              <i className="fas fa-edit"></i> Edit
            </button>
            <button type="button" onClick={onDelete} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold" style={{ background: '#fef2f2', color: '#dc2626' }}>
              <i className="fas fa-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>

      {/* Mobile: compact website-like */}
      <div className="md:hidden">
        <div className="flex gap-3 items-start">
          <div className="w-20 h-14 flex-shrink-0">
            <img src={img} alt={event.title} className="w-full h-full object-cover rounded-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-xs font-medium" style={{ color: '#ea580c' }}>{dateText}</div>
              <Badge type={event.type} />
            </div>
            <h3 className="text-sm font-bold text-gray-800 leading-snug">{event.title}</h3>
          </div>
        </div>
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <div className="flex items-center text-xs text-gray-600 min-w-0">
            <i className="fas fa-map-marker-alt mr-1.5 flex-shrink-0" style={{ color: '#f97316' }}></i>
            <span className="truncate">{event.location}</span>
          </div>
          <a
            href={directionsHref(event)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center text-xs font-medium px-2 py-1 rounded-lg flex-shrink-0"
            style={{ color: '#2563eb', background: '#eff6ff' }}
          >
            <i className="fas fa-directions mr-1"></i>Directions
          </a>
        </div>
        {event.description && <p className="text-gray-700 text-xs leading-relaxed mt-2.5 line-clamp-2">{event.description}</p>}
        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
          <button type="button" onClick={onEdit} className="flex-1 py-2 rounded-lg text-xs font-semibold" style={{ background: '#eff6ff', color: '#2563eb' }}>Edit</button>
          <button type="button" onClick={onDelete} className="flex-1 py-2 rounded-lg text-xs font-semibold" style={{ background: '#fef2f2', color: '#dc2626' }}>Delete</button>
        </div>
      </div>
    </div>
  );
};

const EventModal = ({ mode, initial, onSave, onClose, saving }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const set = (field, val) => { setForm((p) => ({ ...p, [field]: val })); setFormError(''); };

  useEffect(() => {
    setForm(initial || EMPTY_FORM);
  }, [initial]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setFormError('Title is required');
    if (!form.startDate || !form.endDate) return setFormError('Dates are required');
    if (!form.location.trim()) return setFormError('Location is required');
    if (form.endDate < form.startDate) return setFormError('End date cannot be before start date');
    onSave({
      ...form,
      time: form.time || null,
      description: form.description || null,
      imageUrl: form.imageUrl || null,
      mapUrl: form.mapUrl || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-screen overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-800 text-lg">
            {mode === 'edit' ? 'Edit Event' : 'Add New Event'}
          </h3>
          <AdminModalClose onClick={onClose} />
        </div>
        <form onSubmit={handleSubmit} autoComplete="off" className="p-6 space-y-4">
          {formError && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <i className="fas fa-exclamation-circle mr-2"></i>{formError}
            </div>
          )}
          <Input
            label="Event Title"
            required
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            onClear={() => set('title', '')}
            clearable
            placeholder="e.g. Ram Katha & Divine Blessing"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Start Date" required type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            <Input label="End Date" required type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Time (optional)"
              value={form.time || ''}
              onChange={(e) => set('time', e.target.value)}
              onClear={() => set('time', '')}
              clearable
              placeholder="e.g. 6:00 PM - 9:00 PM"
            />
            <AdminFieldSelect
              label="Type"
              value={form.type}
              onChange={(v) => set('type', v)}
              options={[
                { value: 'event', label: 'Event' },
                { value: 'katha', label: 'Katha' },
              ]}
              aria-label="Event type"
            />
          </div>
          <Input
            label="Location"
            required
            value={form.location}
            onChange={(e) => set('location', e.target.value)}
            onClear={() => set('location', '')}
            clearable
            placeholder="Ashram / venue address"
          />
          <Input
            label="Map / Directions URL (optional)"
            value={form.mapUrl || ''}
            onChange={(e) => set('mapUrl', e.target.value)}
            onClear={() => set('mapUrl', '')}
            clearable
            placeholder="https://maps.google.com/..."
            hint="Leave blank to auto-open Google Maps from the location text."
          />
          <Textarea label="Description" value={form.description || ''} onChange={(e) => set('description', e.target.value)} placeholder="Describe the event shown on the website..." />
          <Input
            label="Image URL"
            value={form.imageUrl || ''}
            onChange={(e) => set('imageUrl', e.target.value)}
            onClear={() => set('imageUrl', '')}
            clearable
            placeholder="https://... or /uploads/..."
          />
          {form.imageUrl && (
            <img src={form.imageUrl} alt="preview" className="w-full h-28 object-cover rounded-xl border border-gray-100" onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">
              Date Badge Color
              {mode === 'add' && <span className="ml-2 font-normal text-gray-400">(auto-selected — click to change)</span>}
            </label>
            <div className="flex gap-2 flex-wrap items-center">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set('color', c)}
                  className="w-7 h-7 rounded-full border-2 transition-all"
                  style={{
                    backgroundColor: COLOR_HEX[c],
                    borderColor: form.color === c ? '#111' : 'transparent',
                    transform: form.color === c ? 'scale(1.2)' : 'scale(1)',
                  }}
                  title={c}
                />
              ))}
              <span className="text-xs text-gray-500 capitalize ml-1">{form.color}</span>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 px-4 border border-gray-200 rounded-xl text-gray-600 font-semibold text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 px-4 admin-btn-primary text-sm disabled:opacity-60">
              {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Save Event</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EventsAdmin = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(6);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await eventsApi.getAll({ search, type: typeFilter, month: monthFilter });
      if (res.success) setEvents(res.data);
      else setError(res.message);
    } catch {
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, monthFilter]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { setCurrentPage(1); }, [search, typeFilter, monthFilter, perPage]);

  const totalPages = Math.max(1, Math.ceil(events.length / perPage));
  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return events.slice(start, start + perPage);
  }, [events, currentPage, perPage]);

  const rangeStart = events.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, events.length);

  const openAdd = () => {
    setModal({
      mode: 'add',
      event: { ...EMPTY_FORM, color: nextAutoColor(events.length) },
    });
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const res = modal.mode === 'edit'
        ? await eventsApi.update(modal.event.id, form)
        : await eventsApi.create(form);
      if (!res.success) throw new Error(res.message);
      setModal(null);
      showToast(modal.mode === 'edit' ? '✅ Event updated!' : '✅ Event added!');
      loadEvents();
    } catch (err) {
      alert(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await eventsApi.delete(deleteTarget.id);
      if (!res.success) throw new Error(res.message);
      setDeleteTarget(null);
      showToast('🗑️ Event deleted');
      loadEvents();
    } catch (err) {
      alert(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <Toast message={toast} />

      {/* Header — same outer spacing as Katha admin */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Events & Kathas</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? 'Loading…' : `${events.length} total · showing ${rangeStart}–${rangeEnd}`}
          </p>
        </div>
        <button onClick={openAdd} className="admin-btn-primary px-5 py-2.5 text-sm">
          <i className="fas fa-plus"></i> Add Event
        </button>
      </div>

      {/* Filters + per page */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 mb-5">
        <div className="flex flex-col md:flex-row gap-2 md:items-center">
          <div className="admin-search-wrap flex-1 min-w-0">
            <i className="fas fa-search admin-search-icon" aria-hidden="true"></i>
            <input
              type="search"
              inputMode="search"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title or location..."
              className="admin-search-input"
            />
            {search && (
              <button
                type="button"
                className="admin-search-clear"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            )}
          </div>
          <AdminSelect
            aria-label="Filter by type"
            value={typeFilter}
            onChange={setTypeFilter}
            options={[
              { value: '', label: 'All Types' },
              { value: 'katha', label: 'Katha' },
              { value: 'event', label: 'Event' },
            ]}
          />
          <AdminMonthInput value={monthFilter} onChange={setMonthFilter} />
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-500 whitespace-nowrap">Per page</span>
            <AdminSelect
              aria-label="Items per page"
              value={perPage}
              onChange={setPerPage}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
          <button onClick={loadEvents} className="ml-3 underline">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center"><i className="fas fa-spinner fa-spin text-2xl" style={{ color: '#f97316' }}></i></div>
      ) : events.length === 0 ? (
        <EmptyState
          icon="fas fa-calendar-times"
          title="No events found"
          text="Try clearing filters or add your first event."
          action={
            <button onClick={openAdd} className="px-5 py-2.5 admin-btn-primary text-sm">
              <i className="fas fa-plus mr-2"></i>Add Event
            </button>
          }
        />
      ) : (
        <>
          <div>
            {paginatedEvents.map((ev) => (
              <EventListCard
                key={ev.id}
                event={ev}
                onEdit={() => setModal({ mode: 'edit', event: ev })}
                onDelete={() => setDeleteTarget(ev)}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-gray-500 leading-none">
              Page {currentPage} of {totalPages} · {events.length} event{events.length !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${currentPage === 1 ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-white border border-gray-200 text-gray-700 hover:border-orange-400'}`}
              >
                <i className="fas fa-chevron-left mr-1"></i>Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => totalPages <= 7 || p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => (
                  p === '…' ? (
                    <span key={`e${idx}`} className="px-1 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setCurrentPage(p)}
                      className="w-9 h-9 rounded-lg text-sm font-semibold"
                      style={p === currentPage
                        ? { background: 'linear-gradient(to right, #f97316, #dc2626)', color: '#fff' }
                        : { background: '#fff', color: '#4b5563', border: '1px solid #e5e7eb' }}
                    >
                      {p}
                    </button>
                  )
                ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-white border border-gray-200 text-gray-700 hover:border-orange-400'}`}
              >
                Next<i className="fas fa-chevron-right ml-1"></i>
              </button>
            </div>
          </div>
        </>
      )}

      {modal && (
        <EventModal
          mode={modal.mode}
          initial={modal.event ? {
            title: modal.event.title || '',
            startDate: modal.event.startDate || '',
            endDate: modal.event.endDate || '',
            time: modal.event.time || '',
            location: modal.event.location || '',
            description: modal.event.description || '',
            imageUrl: modal.event.imageUrl || '',
            mapUrl: modal.event.mapUrl || '',
            color: modal.event.color || 'orange',
            type: modal.event.type || 'event',
          } : EMPTY_FORM}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Event?"
          message={`"${deleteTarget.title}" will be permanently removed.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
};

export default EventsAdmin;
